/**
 * Top-level error boundary.
 *
 * React's default behaviour when a render throws is to unmount the whole
 * tree — which shows the user a blank white screen and no hint of what
 * went wrong. This catches the error, logs it to the console, and shows
 * a recovery card so the rest of the shell (sidebar, sign-out) still
 * works.
 *
 * Historical context: the staff role had a slew of white-screens caused
 * by pages trying to format financial fields that the backend strips
 * for non-finance roles (e.g. `formatCurrency(undefined)` →
 * `undefined.toLocaleString()` throws). We've hardened the individual
 * pages, but this boundary is the belt-and-braces guarantee that a
 * missed callsite doesn't brick the SPA for an entire role.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log to the browser console so support can grab it from DevTools.
    // Not sending this to the server — the API logger will have already
    // captured any backend-side cause.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  private reset = () => {
    this.setState({ error: null });
  };

  private reload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div
        style={{
          padding: 40,
          maxWidth: 560,
          margin: "60px auto",
          background: "#fff",
          border: "1px solid var(--border, #e5e7eb)",
          borderRadius: 12,
          boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
        }}
      >
        <h1 style={{ fontSize: 20, marginBottom: 8, color: "var(--text, #111)" }}>
          Something went wrong on this page
        </h1>
        <p style={{ fontSize: 14, color: "var(--text-secondary, #555)", marginBottom: 16 }}>
          The page crashed while rendering. Your session is still active and
          your data is safe. You can try again, or jump back to the dashboard.
        </p>
        {this.state.error.message && (
          <pre
            style={{
              fontSize: 12,
              background: "var(--bg-light, #f6f7f8)",
              padding: 12,
              borderRadius: 8,
              overflow: "auto",
              marginBottom: 16,
              color: "var(--text-secondary, #555)",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {this.state.error.message}
          </pre>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="btn btn-primary"
            onClick={() => {
              this.reset();
              // Route to home so we unmount the broken page's state.
              if (window.location.pathname !== "/") {
                window.location.href = "/";
              }
            }}
          >
            Back to Dashboard
          </button>
          <button className="btn" onClick={this.reload}>
            Reload
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
